<template>
  <div>
    <FormInput v-model="config.path" label="Path" placeholder="customer/Client_Group=ACME/File_Date={run_date}/{run_yyyymmdd}_players.parquet" class="mt-3 w-full" @update:modelValue="emitChange" />
    <div class="text-12 text-semi-dark-text mt-1">
      {run_date} and {run_yyyymmdd} are the run's date. {partition_date} and {partition_yyyymmdd} are each file's date when partitioned. {Variable} inserts a variable's value.
    </div>

    <div class="flex items-center justify-between text-12 font-600 border-b border-solid border-gray-200 pb-1 mt-6">
      <span>Columns</span>
      <div class="flex items-center gap-x-2">
        <PillButton v-if="view === 'Table'" text="Add column" small outlined @click="addColumn" />
        <ButtonToggle :value="view" :items="['Table', 'Text']" size="small" @input="setView" />
      </div>
    </div>
    <div class="text-12 text-semi-dark-text mt-2">
      The columns written to Parquet, in order, with their types. Only these columns are written. A required column must be returned by the tool, an optional one is left out when it is not.
    </div>

    <template v-if="view === 'Text'">
      <FormTextarea v-model="columnsText" label="One column per line" class="mt-3" :placeholder="textPlaceholder" @update:modelValue="onTextChange" />
      <div class="text-12 text-semi-dark-text mt-1">Type "Name type", optionally followed by "nullable" or "optional", or paste contract table rows. Types: {{ arrowTypes.join(', ') }}.</div>
      <div v-for="issue in textIssues" :key="issue.line" class="text-12 text-error mt-1">Line {{ issue.line }}: {{ issue.message }}</div>
      <div v-if="viewError" class="text-12 text-error mt-1">{{ viewError }}</div>
    </template>

    <template v-else>
      <div v-if="!config.columns.length" class="text-12 text-semi-dark-text mt-3">No columns yet. Add them here, or switch to Text to type or paste them.</div>
      <div v-for="(column, index) in config.columns" :key="index" class="flex items-end gap-x-2 mt-2">
        <FormInput v-model="column.name" label="Name" class="flex-grow min-w-120" @update:modelValue="emitChange" />
        <FormSelect v-model="column.type" label="Type" :options="typeOptions" default-reducer :clearable="false" class="w-200 flex-shrink-0" @update:modelValue="emitChange" />
        <div class="flex flex-col items-center pb-1">
          <span class="text-11 text-semi-dark-text">Nullable</span>
          <Toggle v-model="column.nullable" dense @update:modelValue="emitChange" />
        </div>
        <div class="flex flex-col items-center pb-1">
          <span class="text-11 text-semi-dark-text">Required</span>
          <Toggle v-model="column.required" dense @update:modelValue="emitChange" />
        </div>
        <IconButton icon="delete" class="text-primary mb-1" @click="removeColumn(index)" />
      </div>
      <div v-if="duplicateNames.length" class="text-12 text-error mt-2">Each column needs a unique name: {{ duplicateNames.join(', ') }}.</div>
    </template>

    <div v-if="missingRequired.length" class="text-12 text-error mt-2">
      The tool does not return {{ missingRequired.join(', ') }}. The export will fail unless the tool returns them or they are made optional.
    </div>
    <div v-if="missingOptional.length" class="text-12 text-yellow-600 mt-2">The tool does not return {{ missingOptional.join(', ') }}, so they will be left out of the file.</div>

    <FormSelect label="Partition by (optional)" :clearable="true" v-model="config.partition_column" :options="partitionOptions" default-reducer class="mt-6 w-full" @update:modelValue="emitChange" />
    <div class="text-12 text-semi-dark-text mt-1">
      Leave empty to write one file per run. Choose a date32 column to write one file per date in it instead; the path must then use {partition_date} or {partition_yyyymmdd}.
    </div>
  </div>
</template>

<script>
/**
 * Apache Parquet settings for the data export block: destination path, the Arrow
 * column schema and optional date partitioning. Bound with v-model to the block's
 * `parquet_config`.
 *
 * Columns are edited in a Table view or a Text view (one column per line, contract rows
 * can be pasted), which convert both ways. Text is checked line by line, and only a
 * text without problems replaces the columns. When the block has no columns, they are
 * filled from the chosen tool's output columns.
 */
import { ARROW_TYPES, defaultConfig, formatColumnText, parseColumnText } from './apache-parquet';

export default {
  name: 'ApacheParquetExportConfig',
  props: {
    /** The block's `parquet_config`: { path, partition_column, columns }. */
    modelValue: {
      type: Object,
      default: null,
    },
    /** The chosen tool's output columns as block columns, with suggested types. Empty when the tool does not declare them. */
    toolColumns: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  /**
   * @returns {{config: object, view: string, columnsText: string, textIssues: object[], viewError: string}} Local copy of the settings and the editor state.
   */
  data() {
    return {
      config: { ...defaultConfig(), ...JSON.parse(JSON.stringify(this.modelValue || {})) },
      view: 'Table',
      columnsText: '',
      textIssues: [],
      viewError: '',
    };
  },
  computed: {
    /**
     * @returns {string[]} Column types that can be written.
     */
    arrowTypes() {
      return ARROW_TYPES;
    },
    /**
     * @returns {Array<{label: string, value: string}>} Column types for the table view. The long timestamp type gets a
     *   short label so the row stays on one line; the stored value is unchanged.
     */
    typeOptions() {
      return ARROW_TYPES.map((type) => ({ label: type === 'timestamp[us, tz=UTC]' ? 'timestamp (UTC)' : type, value: type }));
    },
    /**
     * @returns {string} Example lines for the text view.
     */
    textPlaceholder() {
      return 'Customer_ID int64\nReg_Date timestamp[us, tz=UTC]\nClosing_Cash_Balance float64 nullable\nDevice string optional\n| `Country_Code` | `string` |';
    },
    /**
     * @returns {Array<{label: string, value: string}>} Named date32 columns, the only ones that can partition.
     */
    partitionOptions() {
      return this.config.columns.filter((column) => column.type === 'date32' && column.name).map((column) => ({ label: column.name, value: column.name }));
    },
    /**
     * @returns {string[]} Column names used more than once in the table view.
     */
    duplicateNames() {
      const seen = new Set();
      const duplicates = new Set();
      this.config.columns.forEach(({ name }) => {
        if (name && seen.has(name)) duplicates.add(name);
        seen.add(name);
      });
      return [...duplicates];
    },
    /**
     * @returns {Set<string>|null} Names the tool returns, or null when the tool does not declare them.
     */
    toolColumnNames() {
      return this.toolColumns.length ? new Set(this.toolColumns.map((column) => column.name)) : null;
    },
    /**
     * @returns {string[]} Required columns the tool does not return, which would fail the export.
     */
    missingRequired() {
      if (!this.toolColumnNames) return [];
      return this.config.columns.filter((column) => column.name && column.required !== false && !this.toolColumnNames.has(column.name)).map((column) => column.name);
    },
    /**
     * @returns {string[]} Optional columns the tool does not return, which are left out of the file.
     */
    missingOptional() {
      if (!this.toolColumnNames) return [];
      return this.config.columns.filter((column) => column.name && column.required === false && !this.toolColumnNames.has(column.name)).map((column) => column.name);
    },
  },
  methods: {
    /**
     * Sends the edited settings to the block.
     * @returns {void}
     */
    emitChange() {
      this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.config)));
    },
    /**
     * Switches between the table and text views. Leaving the text view needs a text
     * without problems, so nothing typed there is silently dropped.
     * @param {string} view Table or Text.
     * @returns {void}
     */
    setView(view) {
      this.viewError = '';
      if (view === this.view) return;
      if (view === 'Table' && this.textIssues.length) {
        this.viewError = 'Fix the lines above to switch to the table.';
        return;
      }
      if (view === 'Text') {
        this.columnsText = formatColumnText(this.config.columns);
        this.textIssues = [];
      }
      this.view = view;
    },
    /**
     * Reads the text view. A text without problems replaces the columns; otherwise the
     * block keeps its last valid columns and the problems are shown per line.
     * @returns {void}
     */
    onTextChange() {
      this.viewError = '';
      const { columns, issues } = parseColumnText(this.columnsText);
      this.textIssues = issues;
      if (issues.length) return;
      this.config.columns = columns;
      this.clearInvalidPartition();
      this.emitChange();
    },
    /**
     * Appends an empty column.
     * @returns {void}
     */
    addColumn() {
      this.config.columns.push({ name: '', type: 'string', nullable: true, required: true });
      this.emitChange();
    },
    /**
     * Removes a column, and the partition setting if it pointed at that column.
     * @param {number} index Position of the column.
     * @returns {void}
     */
    removeColumn(index) {
      this.config.columns.splice(index, 1);
      this.clearInvalidPartition();
      this.emitChange();
    },
    /**
     * Clears the partition setting when its column is no longer a date32 column.
     * @returns {void}
     */
    clearInvalidPartition() {
      if (this.config.partition_column && !this.partitionOptions.some((option) => option.value === this.config.partition_column)) this.config.partition_column = '';
    },
    /**
     * Fills the columns from the chosen tool's output columns.
     * @returns {void}
     */
    fillFromTool() {
      this.config.columns = JSON.parse(JSON.stringify(this.toolColumns));
      this.clearInvalidPartition();
      if (this.view === 'Text') this.columnsText = formatColumnText(this.config.columns);
      this.emitChange();
    },
  },
  watch: {
    toolColumns: {
      /**
       * Fills in the tool's columns when the block has no columns yet.
       * @param {object[]} columns The tool's output columns as block columns.
       * @returns {void}
       */
      handler(columns) {
        if (columns.length && !this.config.columns.length) this.fillFromTool();
      },
      immediate: true,
    },
    modelValue: {
      /**
       * Takes outside changes, such as a different block being opened, without
       * clobbering local edits that the block is simply echoing back.
       * @param {object} value The new settings.
       * @returns {void}
       */
      handler(value) {
        if (JSON.stringify(value || {}) !== JSON.stringify(this.config)) {
          this.config = { ...defaultConfig(), ...JSON.parse(JSON.stringify(value || {})) };
          if (this.view === 'Text') {
            this.columnsText = formatColumnText(this.config.columns);
            this.textIssues = [];
          }
        }
      },
      deep: true,
    },
  },
};
</script>
